import { Helmet } from "react-helmet";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Collapse,
  Divider,
  Grid,
  Paper,
  Step,
  Stepper,
  StepIcon,
  StepLabel,
  StepContent,
  TextField,
  Typography,
  StepConnector,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { makeStyles } from "@mui/styles";
import cssVariables from "../../../theme/cssVariables";
import ApexCharts from "react-apexcharts";

import ConfirmDialog from "../../../components/inbox/ConfirmDialog";
import MessageDialog from "../../../components/inbox/MessageDialog";
import ReadMoreDialog from "../../../components/inbox/ReadMoreDialog";
import { ReactComponent as MarkIcon } from "../../../assets/icons/mark.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check-2.svg";
import { ReactComponent as CancelIcon } from "../../../assets/icons/cancel.svg";
import { ReactComponent as DocIcon } from "../../../assets/icons/document.svg";
import { ReactComponent as MinimizeIcon } from "../../../assets/icons/minimized.svg";
import { ReactComponent as MaximizeIcon } from "../../../assets/icons/maximized.svg";
import { ReactComponent as DownArrowIcon } from "../../../assets/icons/down-arrow.svg";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import planningImg from "../../../assets/images/home/planning.svg";
import personImg from "../../../assets/icons/person-fill.svg";

// rest service
import { getInboxDetail, postWorkflowSubmit, postWorkflowCancel, postWorkflowReject, postWorkflowRequest } from "../../../actions/inbox";
import * as Util from "../../../services/global.service";

const useStyles = makeStyles({
  media: {
    height: 200,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  paperDetailContainer: {
    backgroundColor: `${cssVariables.color.gray}!important`,
    width: "100%",
    padding: "1.5rem",
  },
  blueBg: {
    background: "#6694CC",
  },
  profileContainer: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    marginTop: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  statusBadge: {
    width: "150px",
    borderRadius: "5px",
    padding: "8px 10px",
    textAlign: "center",
  },
  draftBg: {
    background: "#EFB76D26"
  },
  draftText: {
    color: "#EFB76D"
  },
  pendingBg: {
    background: "#F280261A"
  },
  pendingText: {
    color: "#EFB76D"
  },
  approveBg: {
    background: "#5DA19626"
  },
  approveText: {
    color: "#5DA196"
  },
  rejectedBg: {
    background: "#CC605826"
  },
  rejectedText: {
    color: "#CC6058"
  },
  grayBg: {
    background: "#82828226"
  },
  grayText: {
    color: "#828282!important",
  },
  cancelBg: {
    background: "#CC605826"
  },
  cancelText: {
    color: "#CC6058",
  },
  cancelPendingBg: {
    background: "#82828226"
  },
  cancelPendingText: {
    color: "#828282",
  },
  grayStepBg: {
    background: "#828282",
  },
  stepTextRoot: {
    fill: "#939393!important"
  },
  processBg: {
    background: "rgba(239, 183, 109, 0.15)",
  },
  completedBg: {
    background: "rgba(93, 161, 150, 0.15)",
  },
  redBg: {
    background: "rgba(204, 96, 88, 0.15)",
  },
  processText: {
    color: "#EFAD0A!important",
  },
  completedText: {
    color: "#5DA196!important",
  },
  redText: {
    color: "#CC6058!important",
  },
  paddingRight_8: {
    paddingRight: "8px",
  },
  paddingRight_0: {
    paddingRight: "0!important",
  },
  disabledSvg: {
    '& > path': {
      fill: '#A0A0A0'
    },
    '& g > path': {
      fill: '#A0A0A0'
    },
  }
});

function BarLegend(props) {
  const data = props.data;
  let row = [];
  let percent = (data) => {
    let sumpercent = 0
    if (data > 0) {
      sumpercent = ((data / props.total) * 100)
    }
    return sumpercent
  }
  for (var i = 0; i < data.series.length; i++) {
    row.push(
      <Box key={data.series[i].name} display="flex" alignItems="center" sx={{ width: "100%" }}>
        <Box sx={{ width: "10%" }}>
          <div
            className="bar-legend-circle"
            style={{ backgroundColor: data.options.colors[i] }}
          ></div>
        </Box>
        <Box sx={{ width: "40%" }}>
          <Typography style={{
            fontSize: "14px",
            color: "#939393",
          }}> {data.series[i].name}</Typography>
        </Box>
        <Box sx={{ width: "10%" }}>
          <Typography style={{
            fontSize: "14px",
            color: "#939393",
          }}> {data.series[i].data[0].toFixed(2)}</Typography>
        </Box>
        <Box sx={{ width: "20%" }}>
          <Typography style={{
            fontSize: "14px",
            color: "#939393",
          }}> {data.options.unit}</Typography>
        </Box>
        <Box sx={{ width: "10%" }}>
          <Typography style={{
            fontSize: "14px",
            color: "#939393",
          }}>({percent(data.series[i].data[0]).toFixed(2)}%)</Typography>
        </Box>
      </Box>
    );
  }
  return row;
}

function StylePaper(props) {
  return (
    <Paper sx={{ padding: "20px" }} {...props}>
      {props.children}
    </Paper>
  );
}

const CustomStepContent = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  let step = props.data
  let divider = props.divider ?? null
  return (
    <StepContent>
      <Box sx={{ display: "flex" }}>
        {step.employeeStatus === 3 ?
          (
            <div className={`${classes.profileContainer}`} style={{ width: 40, height: 40, background: "linear-gradient(0deg, #CCCCCC, #CCCCCC)" }}>
              <img
                style={{ width: 30, height: 30 }}
                src={personImg}
                alt="profile"
              />
            </div>
          )
          : step.profileImage !== "" || step.profileImage ?
            (
              <img
                className={`${classes.profileContainer}`}
                style={{ width: 40, height: 40, opacity: step.employeeStatus === 2 ? "0.3" : "1" }}
                src={step.employeeStatus === 3 ? personImg : step.profileImage}
                alt="profile"
              />
            ) : <Avatar className={`${classes.profileContainer}`} style={{ width: 40, height: 40, opacity: step.employeeStatus === 2 ? "0.3" : "1" }} />
        }
        <Box
          sx={{
            display: "flex",
            flexFlow: "column",
            marginLeft: "10px",
          }}
        >
          {
            step.employeeStatus === 3 ?
              (
                <React.Fragment>
                  <Typography variant="s14_fw300">
                    ไม่พบข้อมูล
                  </Typography>
                  <Typography variant="s14_fw300">
                    วันที่: -
                  </Typography>
                </React.Fragment>
              )
              : (
                <React.Fragment>
                  <Typography variant="s14_fw300" sx={{ opacity: step.employeeStatus === 2 ? "0.5" : "1" }}>
                    {step.fullname}
                  </Typography>
                  <Typography variant="s14_fw300" sx={{ opacity: step.employeeStatus === 2 ? "0.5" : "1" }}>
                    วันที่: {Util.dateDayTimeFormat(step.date) ?? "-"}
                  </Typography>
                </React.Fragment>
              )
          }
          {step.status ? (
            <div
              className={`${classes.statusBadge} ${classes[Util.StepStatusColor(step) + 'Bg']}`}
            >
              <Typography
                className={`${classes[Util.StepStatusColor(step) + 'Text']}`}
                fontWeight={500}
              >
                {/* { 
                  step.mark === "requester" ?
                    step.status === "pending" ?
                    (t(`label.status.done`))
                    : step.status === "cancel" ?
                    t(`label.status.cancelRequest`)
                    : step.status === "cancel_pending" || step.status === "cancel_cancel" ?
                    t(`label.status.cancelPendingRequest`)
                    : t(`label.status.${step.status}`)
                  : t(`label.status.${step.status}`)
                } */}
                {step.status_name}
              </Typography>
            </div>
          ) : (
            <></>
          )}
          {step.comment ? (
            <React.Fragment>
              <Typography
                variant="h6"
                style={{ marginTop: "10px" }}
              >
                ความคิดเห็น:
              </Typography>
              <Typography
                variant="s14_fw400"
                style={{
                  marginTop: "0",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  color: '#404349'
                }}
                id="readmore"
              >
                {step.comment}
              </Typography>
              <Typography variant="s14_fw400" textAlign="center" onClick={() => props.readmore(step ?? {})} className="clickable"><u>อ่านเพิ่มเติม</u></Typography>
            </React.Fragment>
          ) : (
            <></>
          )}
        </Box>
      </Box>
      {divider ? <Divider sx={{ margin: "10px 0" }} /> : <></>}
    </StepContent>
  )
}

const InboxDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const inboxDetail = useSelector(state => state.Inbox.inboxDetail)

  const setInitData = (value) => {
    let data = value
    // set chart data
    let oData = { ...chart }
    let formatData = ratioData(oData, data)
    let nData = {
      ...oData,
      series: formatData.chartData
    }
    setChartInfo({
      accumulate: formatData.accumulate,
      total: formatData.total,
      min: formatData.min,
    })
    setChart(nData)

    let requestLogs = data.request_logs ?? []
    setRequestLogsData(requestLogs)
    // set workflow 
    formatWorkflowData(data)
  }

  useEffect(() => {
    if (params.serial ?? false) {
      dispatch(getInboxDetail(params.serial)).then((resp) => {
        setInitData(resp)
      })
    } else {
      navigate(`/kpi/home`, { replace: false })
    }
  }, []);

  // chart 
  let chartOptions = {
    type: "bar",
    color: "#939393",
    series: [],
    options: {
      unit: "คะแนน",
      chart: {
        type: "bar",
        stacked: true,
        stackType: "100%",
        toolbar: {
          show: false,
        },
        offsetX: 0,
        offsetY: 0,
        redrawOnWindowResize: true,
        redrawOnParentResize: true,
        width: "100%"
      },
      colors: [
        cssVariables.barChart.blue["200"],
        cssVariables.barChart.green["200"],
        cssVariables.barChart.orange["300"],
        cssVariables.barChart.orange["200"],
      ],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 5,
          barHeight: "100%",
          colors: {
            ranges: [{
              from: 0,
              to: 0,
              color: '#F0F0F0'
            }],
            backgroundBarColors: ['#F0F0F0'],
            backgroundBarOpacity: 1,
            backgroundBarRadius: 0,
          },
        },
      },
      grid: {
        show: false,
        padding: {
          top: -50,
          right: 0,
          bottom: -10,
          left: -20,
        },
      },
      stroke: {
        width: 0,
        colors: ["#fff"],
      },
      xaxis: {
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        x: {
          show: false,
        },
        y: {
          formatter: function (val) {
            return val + " คะแนน";
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
        position: "bottom",
        horizontalAlign: "left",
        offsetX: -33,
        offsetY: -55,
        formatter: function (val, opts) {
          const roundToHundred = (arr) => {
            let output = [];
            let acc = 0;
            for (let i = 0; i < arr.length; i++) {
              let roundedCur = Math.round(arr[i][0]);
              const currentAcc = acc;
              if (acc == 0) {
                output.push(roundedCur);
                acc += arr[i][0];
                continue;
              }
              acc += arr[i][0];
              output.push(Math.round(acc) - Math.round(currentAcc));
            }
            return output;
          };
          let roundedVal = roundToHundred(opts.w.globals.seriesPercent);
          return (
            "<span class='fs-12px ml-2'>" +
            (val ? val : "-") +
            ":</span><span class='fs-12px fw-600 float-right ml-1'>" +
            (opts.w.globals.series[opts.seriesIndex]
              ? opts.w.globals.series[opts.seriesIndex]
              : 0) +
            " (" +
            (roundedVal[opts.seriesIndex] ? roundedVal[opts.seriesIndex] : 0) +
            "%)" +
            "</span>"
          );
        },
      },
    },
  };
  // data for show
  // const currentData = useMemo(() => {
  //   return inboxDetail
  // }, [inboxDetail]);

  const [criteriaScore, setCriteriaScore] = React.useState({});
  const calCriteriaScore = (formular) => {
    let data = { ...criteriaScore }
    return eval(formular)
  }
  // update chart
  const [chart, setChart] = React.useState(chartOptions);
  const [chartInfo, setChartInfo] = React.useState({
    accumulate: 0,
    total: 0,
    min: 0
  });
  const ratioData = (oData, planning) => {
    let result = {
      chartData: [],
      accumulate: 0,
      total: 0,
      min: 0
    }
    let tmpArray = []
    let criteriaScore = {}
    if ((planning.planning ?? false) && (planning.ratio_data ?? false)) {
      for (let i = 0; i < planning.planning.length; i++) {
        for (let j = 0; j < planning.ratio_data.length; j++) {
          if (planning.planning[i].serial == Object.keys(planning.ratio_data[j])) {
            let score = Number(planning.planning[i].is_accumulate === "y" ? Number(planning.planning[i].total_score) + Number(planning.planning[i].accumulate_use) : Number(planning.planning[i].total_score))
            tmpArray.push({ name: planning.planning[i].description, data: [score], seq: planning.planning[i].seq, code: planning.planning[i].code })
            criteriaScore[planning.planning[i].code] = score
            result.total = result.total + score //คะแนนรวมปัจจุบัน
            result.min = Number(planning.ratio_data[j][planning.planning[i].serial].criterion_score)
            if (planning.planning[i].is_accumulate === "y") {
              result.accumulate = result.accumulate + Number(planning.planning[i].accumulate_use) //คะแนนสะสมที่ใช้
            }
          }
        }
      }
    }
    setCriteriaScore(criteriaScore)
    result.chartData = tmpArray.sort((a, b) => a.seq - b.seq)
    return result
  };

  const [workflow, setWorkflow] = React.useState("");
  const formatWorkflowData = (data) => {
    let tmpArray = []
    console.log(data)
    const workflow = data?.workflow ?? false
    if (workflow) {
      let requester = workflow.requester ?? false
      let approver = workflow.approver ?? false
      if (requester) {
        let obj = {
          seq: 0,
          mark: "requester",
          position: "ผู้ส่งคำขออนุมัติ",
          fullname: requester.employee.name ?? "-",
          date: requester.employee.date ?? "",
          status: requester.status ?? "",
          status_name: requester.status_name,
          type: requester.workflow_type,
          profileImage: requester?.employee?.photo ?? "",
          active: true,
          employeeStatus: requester?.employee?.status ? requester.employee.status == "active" ? 1 : 2 : 2,
          comment: requester.employee.remark ?? "",
          stepStatus: requester.status === "cancel" ? "default" : requester.workflow_type === "cancel" || requester.workflow_type === "cancel_pending" || requester.workflow_type === "cancel_cancel" ? "rejected" : requester.workflow_type === "approve" ? "completed" : "default",
        }
        tmpArray.push(obj)
      }

      if (approver) {
        for (const [key, value] of Object.entries(approver)) {
          let stepStatus = "inprogress"
          let isNeed = false
          let obj = {
            seq: value[0].employee.seq ?? key,
            mark: "approver",
            position: value[0].employee.position ?? "-",
            employee: [],
            active: (value[0].complete || value[0].is_current) ? true : false,
            type: value[0].workflow_type,
          }
          console.log("is_need", isNeed)
          value.forEach(item => {
            obj.employee.push({
              fullname: item.employee.name ?? "-",
              date: item.employee.date ?? "",
              status: item.is_current ? item.status : item.status !== "wait" ? item.status : "",
              status_name: item.status_name,
              profileImage: item?.employee?.photo ?? "",
              employeeStatus: item?.employee?.status ? item.employee.status == "active" ? 1 : 2 : 2,
              // comment: "ควรเพิ่มในส่วนของงานที่ตรงกับยุทธ ศาสตร์มากกว่านี้นะคะ ในแผนภาระงาน ควรเพิ่มในส่วนของงานที่ตรงกับยุทธ ศาสตร์มากกว่านี้นะคะ ในแผนภาระงาน ควรเพิ่มในส่วนของงานที่ตรงกับยุทธ ศาสตร์มากกว่านี้นะคะ ในแผนภาระงาน ควรเพิ่มในส่วนของงานที่ตรงกับยุทธ ศาสตร์มากกว่านี้นะคะ ในแผนภาระงาน"
              comment: item.employee.remark ?? ""
            })
            isNeed = item.need_all
            if (requester.status === "cancel") {
              stepStatus = "default"
            } else {
              if (item.complete) {
                stepStatus = "completed"
                if (item.status === "reject") {
                  stepStatus = "rejected"
                }
              } else {
                if (isNeed) {
                  if (item.status === "reject") {
                    stepStatus = "rejected"
                  }
                } else {
                  if (item.status === "approve") {
                    stepStatus = "completed"
                  }
                }
              }
            }
          })
          obj.stepStatus = stepStatus
          tmpArray.push(obj)
        }
      }
    }
    setWorkflow(tmpArray)
  };


  const [requestLogsData, setRequestLogsData] = React.useState([]);
  const [apexChartWidth, setApexChartWidth] = React.useState("0px");

  useEffect(() => {
    setInitData(inboxDetail)
  }, [inboxDetail])


  const [openApproveConfirmDialog, setApproveConfirmDialog] = React.useState(false);
  const handleOpenApproveConfirmDialog = (value) => {
    setApproveConfirmDialog(true);
  };
  const handleCloseApproveConfirmDialog = (value) => {
    setApproveConfirmDialog(false);
  };
  const [openRejectConfirmDialog, setRejectConfirmDialog] = React.useState(false);
  const handleOpenRejectConfirmDialog = (value) => {
    setRejectConfirmDialog(true);
  };
  const handleCloseRejectConfirmDialog = (value) => {
    setRejectConfirmDialog(false);
  };
  const [openCancelConfirmDialog, setCancelConfirmDialog] = React.useState(false);
  const handleOpenCancelConfirmDialog = (value) => {
    setCancelConfirmDialog(true);
  };
  const handleCloseCancelConfirmDialog = (value) => {
    setCancelConfirmDialog(false);
  };
  const [openCancelPendingConfirmDialog, setCancelPendingConfirmDialog] = React.useState(false);
  const handleOpenCancelPendingConfirmDialog = (value) => {
    setCancelPendingConfirmDialog(true);
  };
  const handleCloseCancelPendingConfirmDialog = (value) => {
    setCancelPendingConfirmDialog(false);
  };

  const [openCancelCancelConfirmDialog, setCancelCancelConfirmDialog] = React.useState(false);
  const handleOpenCancelCancelConfirmDialog = (value) => {
    setCancelCancelConfirmDialog(true);
  };
  const handleCloseCancelCancelConfirmDialog = (value) => {
    setCancelCancelConfirmDialog(false);
  };


  const [messageSuccess, setMessageSuccess] = React.useState("");
  const [failMessage, setMessageFail] = React.useState("");

  const handleApproval = (value) => {
    let approveData = {
      "rb_pa_workflow_assign": inboxDetail?.workflow_assign ?? "",
      "remark": value.description
    }
    dispatch(postWorkflowSubmit(approveData)).then(() => {
      dispatch(getInboxDetail(params.serial))
      setApproveConfirmDialog(false)
      setMessageSuccess("การตรวจสอบคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" สำเร็จ")
      handleOpenSucceedMessageDialog(true)
    }).catch(() => {
      setApproveConfirmDialog(false)
      setMessageFail("กรุณาตรวจสอบคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" \nใหม่อีกครั้ง")
      handleOpenFailedMessageDialog(true)
      // dispatch(getInboxDetail(params.serial))
    })
  };
  const handleReject = (value) => {
    let rejectData = {
      "rb_pa_workflow_assign": inboxDetail?.workflow_assign ?? "",
      "remark": value.description
    }
    dispatch(postWorkflowReject(rejectData)).then(() => {
      dispatch(getInboxDetail(params.serial))
      setRejectConfirmDialog(false)
      setMessageSuccess("การปฏิเสธคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" สำเร็จ")
      handleOpenSucceedMessageDialog(true)
    }).catch(() => {
      setRejectConfirmDialog(false)
      setMessageFail("กรุณาปฏิเสธคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" \nใหม่อีกครั้ง")
      handleOpenFailedMessageDialog(true)
      // dispatch(getInboxDetail(params.serial))
    })

  };
  const handleCancel = (value) => {
    let cancelData = {
      "rb_pa_evaluate_id": inboxDetail?.evaluate ?? "",
      "remark": value.description
    }
    dispatch(postWorkflowCancel(cancelData)).then(() => {
      dispatch(getInboxDetail(params.serial))
      setCancelConfirmDialog(false)
      setMessageSuccess("การถอนคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" สำเร็จ")
      handleOpenSucceedMessageDialog(true)
    }).catch(() => {
      setCancelConfirmDialog(false)
      setMessageFail("กรุณาถอนคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" \nใหม่อีกครั้ง")
      handleOpenFailedMessageDialog(true)
      // dispatch(getInboxDetail(params.serial))
    })
  };
  const handleCancelCancel = (value) => {
    let cancelData = {
      "rb_pa_evaluate_id": inboxDetail?.evaluate ?? "",
      "remark": value.description
    }
    dispatch(postWorkflowCancel(cancelData)).then(() => {
      dispatch(getInboxDetail(params.serial))
      setCancelConfirmDialog(false)
      setMessageSuccess("การถอนการยกเลิก\n \"จัดทำข้อตกลงภาระงาน\" สำเร็จ")
      handleOpenSucceedMessageDialog(true)
    }).catch(() => {
      setCancelConfirmDialog(false)
      setMessageFail("กรุณาถอนการยกเลิก\n \"จัดทำข้อตกลงภาระงาน\" \nใหม่อีกครั้ง")
      handleOpenFailedMessageDialog(true)
      // dispatch(getInboxDetail(params.serial))
    })
  };
  const handlePendingCancel = (value) => {
    let requestData = {
      "rb_pa_evaluate_id": inboxDetail?.evaluate ?? "",
      "remark": value.description
    }
    dispatch(postWorkflowRequest(requestData)).then(() => {
      dispatch(getInboxDetail(params.serial))
      setCancelPendingConfirmDialog(false)
      setMessageSuccess("การยกเลิกคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" สำเร็จ")
      handleOpenSucceedMessageDialog(true)
    }).catch(() => {
      setCancelPendingConfirmDialog(false)
      setMessageFail("กรุณายกเลิกคำขออนุมัติ\n \"จัดทำข้อตกลงภาระงาน\" \nใหม่อีกครั้ง")
      handleOpenFailedMessageDialog(true)
    })
  };
  // message dialog
  const [openSucceedMessageDialog, setSucceedMessageDialog] = React.useState(false);
  const handleOpenSucceedMessageDialog = (value) => {
    setSucceedMessageDialog(true)
  };
  const handleCloseSucceedMessageDialog = (value) => {
    setSucceedMessageDialog(false);
    navigate(`/app/inbox`, { replace: false })
  };
  const [openFailedMessageDialog, setFailedMessageDialog] = React.useState(false);
  const handleOpenFailedMessageDialog = (value) => {
    setFailedMessageDialog(true);
  };
  const handleCloseFailedMessageDialog = (value) => {
    setFailedMessageDialog(false);
    navigate(`/app/inbox`, { replace: false })
  };
  // read more dialog
  // handleOpenReadMoreDialog
  const [readMoreComment, setReadMoreComment] = React.useState("");
  const [openReadMoreDialog, setReadMoreDialog] = React.useState(false);
  const handleOpenReadMoreDialog = (value) => {
    setReadMoreComment(value)
    setReadMoreDialog(true);
  };
  const handleCloseReadMoreDialog = (value) => {
    setReadMoreDialog(false);
  };

  const classes = useStyles();

  const styles = {
    greenBadge: {
      background: "rgb(186 217 87 / 15%)",
      color: "#93AC41",
      padding: "2px 5px",
      marginTop: "21px",
      width: "fit-content"
    },
    blockGrid: {
      padding: "38px 0 0 0",
    },
  };

  const [activeStep, setActiveStep] = React.useState(0);
  const [toggleSteppers, setToggleSteppers] = React.useState(true);
  const [requestLogToggle, setRequestLogToggle] = React.useState(false);

  useEffect(() => {
    let apex = document.getElementById("apexchart")
    setApexChartWidth(apex.parentElement.clientWidth + "px")
  }, [toggleSteppers])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleGoToPlanningDetail = (serial) => {
    if (serial !== "") {
      navigate(`/app/planning/${serial}`, { replace: false })
    }
  }
  const handleSteppersTransition = () => {
    setToggleSteppers(!toggleSteppers);
  };

  const handleToggleRequestLog = () => {
    setRequestLogToggle(!requestLogToggle);
  };

  const inboxType = location.pathname.split("/")

  return (
    <>
      <Helmet>
        <title>Inbox | PMS </title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Grid container mt={1} mb={1}>
            <Typography fontSize={16} lineHeight={1} color="#68717A"><span style={{ color: "#5DA196", cursor: "pointer", }} onClick={() => navigate('/kpi/home')}>{t('label.home')}</span> / <span style={{ color: "#5DA196", cursor: "pointer", }} onClick={() => navigate('/app/inbox')}>{t('label.inbox')}</span>  / <span style={{ color: "#A3A3A3" }} > {inboxType[3] === "received" ? "ได้รับ" : "ส่ง"} </span></Typography>
          </Grid>
          <Grid container spacing={2} mt={1} mb={0}>
            <Grid item>
              <Typography variant="h2">
                {/* {t("home.label.latestInbox")} */}
                {inboxType[3] === "received" ? "ได้รับ" : "ส่ง"}
              </Typography>
            </Grid>
            <Grid item xs="auto" ml="auto">
              <Box display="flex" sx={{ width: "fit-content" }}>
                {
                  inboxDetail?.action ?? false ?
                    inboxDetail.action.map(item => {
                      if (item === "reject") {
                        return (
                          <React.Fragment>
                            <Button
                              color="red"
                              variant="contained"
                              fullWidth
                              startIcon={<MarkIcon />}
                              className="btn-large"
                              sx={{ marginRight: 1 }}
                              onClick={handleOpenRejectConfirmDialog}
                            >
                              ปฏิเสธ
                            </Button>
                          </React.Fragment>
                        )
                      } else if (item === "verify") {
                        return (
                          <React.Fragment>
                            <Button
                              color="primary"
                              variant="contained"
                              fullWidth
                              startIcon={<CheckIcon />}
                              className="btn-large"
                              onClick={handleOpenApproveConfirmDialog}
                            >
                              ตรวจสอบ
                            </Button>
                          </React.Fragment>
                        )
                      } else if (item === "approve") {
                        return (
                          <React.Fragment>
                            <Button
                              color="primary"
                              variant="contained"
                              fullWidth
                              startIcon={<CheckIcon />}
                              className="btn-large"
                              onClick={handleOpenApproveConfirmDialog}
                            >
                              อนุมัติปปป
                            </Button>
                          </React.Fragment>
                        )
                      } else if (item === "cancel") {
                        return (
                          <React.Fragment>
                            <Button
                              color="red"
                              variant="contained"
                              fullWidth
                              startIcon={<MarkIcon />}
                              className="btn-large"
                              onClick={handleOpenCancelConfirmDialog}
                            >
                              ถอนคำขออนุมัติ
                            </Button>
                          </React.Fragment>
                        )
                      } else if (item === "pending_cancel") {
                        return (
                          <React.Fragment>
                            <Button
                              color="red"
                              variant="contained"
                              fullWidth
                              startIcon={<MarkIcon />}
                              className="btn-large"
                              onClick={handleOpenCancelPendingConfirmDialog}
                            >
                              ขอยกเลิกอนุมัติ
                            </Button>
                          </React.Fragment>
                        )
                      } else if (item === "cancel_cancel") {
                        return (
                          <React.Fragment>
                            <Button
                              color="red"
                              variant="outlined"
                              fullWidth
                              startIcon={<CancelIcon />}
                              className="btn-large"
                              onClick={handleOpenCancelCancelConfirmDialog}
                            >
                              ถอนการยกเลิก
                            </Button>
                          </React.Fragment>
                        )
                      } else {
                        return <></>
                      }
                    })
                    : <></>
                }
              </Box>
            </Grid>
            {/* <Grid ml={2} item display="flex" alignItems="center" justifyContent="center">
              <Typography>1/2</Typography>
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center">
              <ChevronLeftIcon fontSize="large" className={`${classes.disabledSvg} clickable`}></ChevronLeftIcon>
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center">
              <ChevronRightIcon fontSize="large" className={`clickable`}></ChevronRightIcon>
            </Grid> */}
          </Grid>
          <Grid container spacing={2} mt={0} mb={2} wrap="nowrap">
            {/* left box - detail */}
            <Grid item xs={toggleSteppers ? 9 : true}
              sm={toggleSteppers ? 9 : true}
              md={toggleSteppers ? 9 : true}
              lg={toggleSteppers ? 9 : true}>
              <StylePaper elevation={3}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {
                      inboxDetail?.employee?.photo ?? false ?
                        <img
                          className={`${classes.profileContainer}`}
                          src={inboxDetail.employee.photo}
                          alt="profile"
                          onerror={`this.onerror=null;this.src=${personImg}`}
                        /> : <Avatar className={`${classes.profileContainer}`} />
                    }
                  </Grid>
                  <Grid item xs sm>
                    <Grid container py={1}>
                      <Grid item xs sm>
                        <Grid container>
                          <Grid item display="flex">
                            <Typography
                              variant="h3"
                              color="primary"
                              sx={{ marginTop: "auto" }}
                            >
                              {inboxDetail?.employee?.fullname ?? "-"}
                            </Typography>
                          </Grid>
                          <Grid item ml="auto" xs="auto" sm="auto">
                            <div
                              className={`${classes.statusBadge} ${inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างการแก้ไข" || inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างดำเนินการ"
                                ? classes.draftBg
                                : inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างการอนุมัติ"
                                  ? classes.pendingBg
                                  : inboxDetail?.rb_pa_data?.[0]?.status_description === "อนุมัติเสร็จสิ้น"
                                    ? classes.approveBg
                                    : inboxDetail?.rb_pa_data?.[0]?.status_description === "ปฏิเสธ"
                                      ? classes.rejectedBg
                                      : inboxDetail?.rb_pa_data?.[0]?.status_description === "ยกเลิกเสร็จสิ้น"
                                        ? classes.cancelBg
                                        : classes.cancelPendingBg
                                }`}
                            >
                              <Typography
                                className={
                                  inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างการแก้ไข" || inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างดำเนินการ"
                                    ? classes.draftText
                                    : inboxDetail?.rb_pa_data?.[0]?.status_description === "ระหว่างการอนุมัติ"
                                      ? classes.pendingText
                                      : inboxDetail?.rb_pa_data?.[0]?.status_description === "อนุมัติเสร็จสิ้น"
                                        ? classes.approveText
                                        : inboxDetail?.rb_pa_data?.[0]?.status_description === "ปฏิเสธ"
                                          ? classes.rejectedText
                                          : inboxDetail?.rb_pa_data?.[0]?.status_description === "ยกเลิกเสร็จสิ้น"
                                            ? classes.cancelText
                                            : classes.cancelPendingText
                                }
                                fontWeight={500}
                                variant="h6"
                              >
                                {inboxDetail?.rb_pa_data?.[0]?.status_description ?? "-"}
                              </Typography>
                            </div>
                          </Grid>
                          <Grid item xs="auto" sm="auto" md="auto" ml={3}>
                            <Button
                              color="black"
                              variant="outlined"
                              fullWidth
                              startIcon={<DocIcon />}
                              className="btn-large"
                              onClick={() => handleGoToPlanningDetail(inboxDetail?.evaluate ?? "")}
                            >
                              <Typography variant="h6" color="black">
                                ดูรายละเอียด
                              </Typography>
                            </Button>
                          </Grid>
                        </Grid>
                        <Grid container mt={0}>
                          <Grid item xs="auto" sm="auto">
                            <Typography variant="label">
                              วางแผนข้อตกลงภาระงาน
                            </Typography>
                          </Grid>
                          {/* <Grid item ml="auto">
                            <Typography
                              variant="sub_label"
                              color="black"
                              fontSize={14}
                            >
                              { dateDayTimeFormat(inboxDetail?.rb_pa_data?.[0]?.start) ?? "-" }
                            </Typography>
                          </Grid> */}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {
                  requestLogsData.length > 0 ?
                    <Grid container mt={2}>
                      <Grid item xs md lg>
                        <Box sx={{
                          border: `1px solid ${requestLogsData[0].status === 'request' || requestLogsData[0].status === 'verified' || requestLogsData[0].status === 'approved' ? '#5DA196' : '#CC6058'}`
                          , borderRadius: "10px"
                        }}>
                          <Collapse in={requestLogToggle} collapsedSize={45} sx={{ padding: "10px", position: "relative" }}>
                            <div style={{ position: "absolute", borderLeft: `0.5px dashed #BBBEBE`, top: "50%", transform: "translate(-50%, -50%)", height: "calc(100% - 40px)", marginLeft: "6.5px", zIndex: "0" }}></div>
                            <Grid container spacing={2}>
                              <Grid item display="flex" alignItems="center" justifyContent="center">
                                <div style={{ background: `${requestLogsData[0].status === 'request' || requestLogsData[0].status === 'verified' || requestLogsData[0].status === 'approved' ? '#5DA196' : '#CC6058'}`, borderRadius: "50%", width: "12px", height: "12px", zIndex: "1" }}></div>
                              </Grid>
                              <Grid item display="flex" alignItems="center" justifyContent="center">
                                <Typography color={`${requestLogsData[0].status === 'request' || requestLogsData[0].status === 'verified' || requestLogsData[0].status === 'approved' ? '#5DA196' : '#CC6058'}`}>{t("label.request.status")} : {t(`label.status.requestLog.${requestLogsData[0].status}`)} ({Util.dateDayTimeFormat(requestLogsData[0].created_at)})</Typography>
                              </Grid>
                              {
                                requestLogsData.length > 1 ?
                                  <Grid item xs sm md lg sl display="flex" alignItems="center" justifyContent="flex-end">
                                    <Box sx={{ transform: requestLogToggle ? "rotate(180deg)" : "rotate(0deg)", transition: "0.4s" }}>
                                      <DownArrowIcon onClick={handleToggleRequestLog} className="clickable"></DownArrowIcon>
                                    </Box>
                                  </Grid> : <></>
                              }
                            </Grid>
                            {
                              requestLogsData.slice(1).map(item =>
                                <Grid container spacing={2} mt={1}>
                                  <Grid item display="flex" alignItems="center" justifyContent="center">
                                    <div style={{ background: `${item.status === 'request' || item.status === 'verified' || item.status === 'approved' ? '#5DA196' : '#CC6058'}`, borderRadius: "50%", width: "12px", height: "12px", zIndex: "1" }}></div>
                                  </Grid>
                                  <Grid item display="flex" alignItems="center" justifyContent="center">
                                    <Typography color={`${item.status === 'request' || item.status === 'verified' || item.status === 'approved' ? '#5DA196' : '#CC6058'}`}>{t("label.request.status")} : {t(`label.status.requestLog.${item.status}`)} ({Util.dateDayTimeFormat(item.created_at)})</Typography>
                                  </Grid>
                                </Grid>
                              )
                            }
                          </Collapse>
                        </Box>
                      </Grid>
                    </Grid>
                    : <></>
                }
                <Grid container mt={1}>
                  <Grid item xs sm md>
                    <Paper
                      elevation={0}
                      classes={{ root: classes.paperDetailContainer }}
                    >
                      <Grid container>
                        <Grid item xs sm md>
                          <Typography variant="h3">
                            สรุปแผนข้อตกลงภาระงาน
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container mt={2}>
                        <Grid item xs sm md>
                          <Card sx={{ width: "100%" }}>
                            <Box style={{ height: "100%" }}>
                              <CardContent>
                                <Grid container spacing={0}>
                                  <Grid item>
                                    <Grid container>
                                      <Grid item>
                                        <Typography variant="h4">
                                          {inboxDetail?.rb_pa_data?.[0]?.description ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container mt={2}>
                                      <Grid item mr={1}>
                                        <div
                                          className={`${classes.iconContainer} ${classes.blueBg}`}
                                        >
                                          <img
                                            src={planningImg}
                                            alt="bannerImage"
                                          />
                                        </div>
                                      </Grid>
                                      <Grid item>
                                        <Typography
                                          variant="h4"
                                          sx={{
                                            color: cssVariables.color.blue,
                                          }}
                                        >
                                          วางแผนข้อตกลงภาระงาน
                                        </Typography>
                                        <Typography fontSize={15} color="gray">
                                          แผนข้อตกลงภาระงาน
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item ml="auto" xs={4} sm={4} md={4}>
                                    <Grid container mb={1}>
                                      <Grid item xs={6} sm={6} md={6}>
                                        <Typography variant="sub_label">
                                          ระดับบุคลากร:
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={6}>
                                        <Typography variant="sub_label_black">
                                          {inboxDetail?.employee?.empsubgroup ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container mb={1}>
                                      <Grid item xs={6} sm={6} md={6}>
                                        <Typography variant="sub_label">
                                          สายงาน:
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={6}>
                                        <Typography variant="sub_label_black">
                                          {inboxDetail?.employee?.linegroup ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container mb={1}>
                                      <Grid item xs={6} sm={6} md={6}>
                                        <Typography variant="sub_label">
                                          ตำแหน่งงาน:
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={6}>
                                        <Typography variant="sub_label_black">
                                          {inboxDetail?.employee?.position ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container mb={1}>
                                      <Grid item xs={6} sm={6} md={6}>
                                        <Typography variant="sub_label">
                                          หน่วยงาน:
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={6}>
                                        <Typography variant="sub_label_black">
                                          {inboxDetail?.employee?.organization ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container mb={1}>
                                      <Grid item xs={6} sm={6} md={6}>
                                        <Typography variant="sub_label">
                                          คะแนนสะสมที่ยกมาได้:
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={6}>
                                        <Typography variant="sub_label_black">
                                          {inboxDetail?.employee?.accumulate_score ? inboxDetail.employee.accumulate_score !== "" ? inboxDetail.employee.accumulate_score : "0" : "0"} คะแนน
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                      {/* chart */}
                      <Grid container spacing={2} mt={1}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Card classes={{ root: classes.customCardSize }}>
                            <Box px={2} style={{ height: "100%" }}>
                              <CardContent
                                className={classes.cardContentSize}
                                sx={{
                                  justifyContent: "flex-start",
                                  alignItems: "flex-start",
                                  padding: "10px 0",
                                }}
                              >
                                <Grid container my={2}>
                                  <Grid item lg={6}>
                                    <Typography
                                      fontSize={18}
                                      fontWeight={500}
                                      lineHeight="19.8px"
                                    >
                                      สัดส่วนแผนข้อตกลงภาระงาน
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={6}>
                                    <Typography
                                      fontSize={18}
                                      fontWeight={500}
                                      lineHeight="24.84px"
                                      textAlign="right"
                                      sx={{ color: "#939393" }}
                                    >
                                      {chartInfo.total > 0 ? `100%` : `0%`}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid container>
                                  <Grid item lg={12} md={12} sm={12} xs={12} sx={{ width: "100%" }}>
                                    <ApexCharts
                                      options={chart.options}
                                      series={chart.series}
                                      type={chart.type}
                                      height="50px"
                                      width={apexChartWidth}
                                      id="apexchart"
                                    ></ApexCharts>
                                  </Grid>
                                </Grid>
                                <Grid container>
                                  <Grid item lg={8} md={8} xs={12}>
                                    {/* <Grid container>
                                      <Grid item> */}
                                    <BarLegend
                                      data={chart}
                                      total={chartInfo.total}
                                    ></BarLegend>
                                    {/* { BarLegend(chartOptions) } */}
                                    {/* </Grid>
                                    </Grid> */}
                                  </Grid>
                                  <Grid
                                    item
                                    md={4}
                                    lg={4}
                                    xs={12}
                                    display="flex"
                                    flexDirection="column"
                                  >
                                    {
                                      chartInfo.accumulate > 0 ?
                                        <div style={styles.greenBadge}>
                                          <Typography fontSize={12} fontWeight={500} lineHeight="18px" >
                                            ใช้คะแนนสะสม {chartInfo.accumulate} คะแนน
                                            {/*{t("home.label.detail")}*/}
                                          </Typography>
                                        </div> : <></>
                                    }
                                  </Grid>
                                </Grid>
                              </CardContent>
                              <Divider variant="middle" />
                              <CardActions>
                                <Grid container mt={0}>
                                  <Grid
                                    item
                                    display="flex"
                                    justifyContent="flex-end"
                                    sx={{
                                      flexFlow: "column",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    <Typography
                                      fontSize={16}
                                      lineHeight="24px"
                                      fontWeight={500}
                                      textAlign="right"
                                      sx={{ color: "#939393" }}
                                    >
                                      {/* {t("home.label.title")} */}
                                      คะแนนสะสมรวมปัจจุบัน:{" "}
                                      <span
                                        className={classes.styleScroll}
                                        style={{
                                          marginLeft: "10px",
                                          color: cssVariables.color.primary,
                                        }}
                                      >
                                        {chartInfo.total.toFixed(2)}
                                      </span>{" "}
                                      คะแนน
                                    </Typography>
                                    <Typography
                                      fontSize={11}
                                      lineHeight="16.5px"
                                      textAlign="left"
                                      sx={{ color: "#939393" }}
                                    >
                                      {/* {t("home.label.title")} */}
                                      (ภาระงานขั้นต่ำ {chartInfo.min} คะแนน)
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </CardActions>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                      {/* score */}
                      <Grid container spacing={2} mt={1}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Card classes={{ root: classes.customCardSize }}>
                            <Box px={2} style={{ height: "100%" }}>
                              <CardContent
                                className={classes.cardContentSize}
                                sx={{
                                  justifyContent: "flex-start",
                                  alignItems: "flex-start",
                                  padding: "10px 0",
                                }}
                              >
                                <Grid container my={2}>
                                  <Grid item lg={6}>
                                    <Typography
                                      fontSize={18}
                                      fontWeight={500}
                                      lineHeight="19.8px"
                                    >
                                      {t('planning.label.ruleAndCondition')}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid container mb={1}>
                                  <Grid item lg={9} xs={9}>
                                    <Typography variant="sub_label">
                                      ประเภทข้อตกลงภาระงาน
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={1} xs={1} textAlign="center">
                                    <Typography variant="sub_label">
                                      ต่ำสุด
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={1} xs={1} textAlign="center">
                                    <Typography variant="sub_label">
                                      สูงสุด
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={1} xs={1} textAlign="center">
                                    <Typography variant="sub_label">
                                      สัดส่วนที่ได้
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid container>
                                  <Grid item lg={12} xs={12}>
                                    <Divider />
                                  </Grid>
                                </Grid>
                                {inboxDetail.criteria ?? false ?
                                  inboxDetail.criteria.length > 0 ?
                                    inboxDetail.criteria.sort((a, b) => {
                                      return a.seq - b.seq
                                    }).map(item => {
                                      return (<Grid container mt={1}>
                                        <Grid item lg={9} xs={9}>
                                          <Typography
                                            variant="label"
                                            fontWeight={400}
                                          >
                                            {item.description}
                                          </Typography>
                                        </Grid>
                                        <Grid item lg={1} xs={1} textAlign="center">
                                          <Typography
                                            variant="label"
                                            fontWeight={400}
                                          >
                                            {item.min > 0 ? item.min : "-"}
                                          </Typography>
                                        </Grid>
                                        <Grid item lg={1} xs={1} textAlign="center">
                                          <Typography
                                            variant="label"
                                            fontWeight={400}
                                          >
                                            {item.max > 0 ? item.max : "-"}
                                          </Typography>
                                        </Grid>
                                        <Grid item lg={1} xs={1} textAlign="center">
                                          {
                                            item.action === "error" ?
                                              (<Typography color={calCriteriaScore(item.sum) >= item.min ? item.max > 0 ? calCriteriaScore(item.sum) <= item.max ? "#5DA196" : "#CC6058" : "#5DA196" : "#CC6058"} fontSize={16} lineHeight="21.86px">
                                                {calCriteriaScore(item.sum) !== undefined && calCriteriaScore(item.sum) !== null ? calCriteriaScore(item.sum).toFixed(2) : 0}
                                              </Typography>) :
                                              (<Typography color={calCriteriaScore(item.sum) >= item.min ? item.max > 0 ? calCriteriaScore(item.sum) <= item.max ? "#5DA196" : "#CC6058" : "#5DA196" : "#FFBF49"} fontSize={16} lineHeight="21.86px">
                                                {calCriteriaScore(item.sum) !== undefined && calCriteriaScore(item.sum) !== null ? calCriteriaScore(item.sum).toFixed(2) : 0}
                                              </Typography>)
                                          }
                                        </Grid>
                                      </Grid>)
                                    })
                                    : <></>
                                  : <></>
                                }
                              </CardContent>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </StylePaper>
            </Grid>
            {/* right box - stepper */}
            <Grid
              item
              xs={toggleSteppers ? 3 : "auto"}
              sm={toggleSteppers ? 3 : "auto"}
              md={toggleSteppers ? 3 : "auto"}
              lg={toggleSteppers ? 3 : "auto"}
            >
              <StylePaper
                elevation={3}
                sx={{
                  marginLeft: "auto",
                  padding: "20px",
                  width: toggleSteppers ? "100%" : "fit-content",
                }}
              >
                <Box sx={{ maxWidth: 400 }} id="steppers">
                  <Box sx={{ display: "flex" }}>
                    {toggleSteppers ? (
                      <Typography variant="h3">สถานะ</Typography>
                    ) : (
                      <></>
                    )}
                    <Button
                      size="small"
                      color="black"
                      variant="outlined"
                      sx={{
                        width: "30px",
                        height: "30px",
                        padding: "0",
                        minWidth: "auto",
                        marginLeft: "auto",
                      }}
                      onClick={handleSteppersTransition}
                    >
                      {toggleSteppers ? (
                        <MinimizeIcon></MinimizeIcon>
                      ) : (
                        <MaximizeIcon></MaximizeIcon>
                      )}
                    </Button>
                  </Box>
                  <Stepper
                    orientation="vertical"
                    sx={{ flexDirection: "column-reverse", overflow: "auto", maxHeight: "inherit", marginTop: "10px" }}
                    connector={
                      toggleSteppers ? (
                        <StepConnector />
                      ) : (
                        <StepConnector sx={{ margin: "0 auto" }} />
                      )
                    }
                  >
                    {
                      workflow.length > 0 ?
                        workflow.map((step, index) => (
                          <Step
                            key={step.label}
                            expanded={true}
                            active={step.active}
                            last={index == 0 ? true : false}
                            sx={
                              !toggleSteppers
                                ? { display: "flex", margin: "0 auto" }
                                : {}
                            }
                          >
                            <StepLabel
                              StepIconProps={{
                                classes: {
                                  active:
                                    step.stepStatus === "inprogress"
                                      ? classes.processText
                                      : step.stepStatus === "completed"
                                        ? classes.completedText
                                        : step.stepStatus === "rejected"
                                          ? classes.redText
                                          : classes.grayText,
                                  text: !step.active ? classes.stepTextRoot : "",
                                },
                              }}
                              classes={{
                                iconContainer: toggleSteppers
                                  ? classes.paddingRight_8
                                  : classes.paddingRight_0,
                              }}
                            >
                              {toggleSteppers ? (
                                <Typography fontWeight={600}>{step.position}</Typography>
                              ) : (
                                ""
                              )}
                            </StepLabel>
                            {
                              toggleSteppers ?
                                step.mark === "approver" ?
                                  (step.employee ?? false) && (step.employee.length > 0 ?? false) ?
                                    step.employee.map((item, index) => {
                                      let isDivide = step.employee.length - 1 === index ? false : true
                                      {/* console.log(item) */ }
                                      return (
                                        <CustomStepContent data={item} readmore={handleOpenReadMoreDialog} divider={isDivide} />
                                      )
                                    })
                                    : (
                                      <></>
                                    )
                                  : (
                                    <CustomStepContent data={step} readmore={handleOpenReadMoreDialog} divider={false} />
                                  )
                                : <></>
                            }
                          </Step>
                        )) : (<></>)
                    }
                  </Stepper>
                </Box>
              </StylePaper>
            </Grid>
          </Grid>
          <ConfirmDialog open={openRejectConfirmDialog} action={handleReject} setValue={handleCloseRejectConfirmDialog} type="reject" title="ยืนยันการปฏิเสธคำขออนุมัติ" text={"ต้องการยืนยันการปฏิเสธคำขออนุมัติ \"จัดทำข้อตกลงภาระงาน\" หรือไม่ ?"} />
          <ConfirmDialog open={openApproveConfirmDialog} action={handleApproval} setValue={handleCloseApproveConfirmDialog} type="approve" title="ยืนยันการตรวจสอบคำขออนุมัติ" text={"ต้องการยืนยันการตรวจสอบคำขออนุมัติ \"จัดทำข้อตกลงภาระงาน\" หรือไม่ ?"} />
          <ConfirmDialog open={openCancelConfirmDialog} action={handleCancel} setValue={handleCloseCancelConfirmDialog} type="reject" title="ยืนยันการถอนคำขออนุมัติ" text={"ต้องการยืนยันการถอนคำขออนุมัติ \"จัดทำข้อตกลงภาระงาน\" หรือไม่ ?"} />
          <ConfirmDialog open={openCancelPendingConfirmDialog} action={handlePendingCancel} setValue={handleCloseCancelPendingConfirmDialog} type="reject" title="ยืนยันการยกเลิกคำขออนุมัติ" text={"ต้องการยืนยันการยกเลิกคำขออนุมัติ \"จัดทำข้อตกลงภาระงาน\" หรือไม่ ?"} />
          <ConfirmDialog open={openCancelCancelConfirmDialog} action={handleCancelCancel} setValue={handleCloseCancelCancelConfirmDialog} type="reject" title="ยืนยันการถอนการยกเลิก" text={"ต้องการยืนยันการถอนการยกเลิกนี้หรือไม่ ?"} />

          <MessageDialog open={openSucceedMessageDialog} setValue={handleCloseSucceedMessageDialog} type="success" title="ดำเนินการสำเร็จ" text={messageSuccess} />
          <MessageDialog open={openFailedMessageDialog} setValue={handleCloseFailedMessageDialog} type="fail" title="ดำเนินการไม่สำเร็จ" text={failMessage} />
          <ReadMoreDialog open={openReadMoreDialog} setValue={handleCloseReadMoreDialog} title="เพิ่มเติม" data={readMoreComment} />
        </Container>
      </Box>
    </>
  );
};
export default InboxDetail;
